/* eslint-disable no-console */
import type { RateShieldContext } from '../context'
import type {
  BlockRecord,
  FixedWindowEntry,
  SlidingWindowEntry
} from '../stores/types'
import { MemoryStore } from '../stores/MemoryStore'
import type {
  AllowedEvent,
  BlockedEvent,
  LimitReachedEvent,
  RateLimitInfo,
  RateLimitOptions,
  RateLimitStats,
  RateLimiterCore,
  RecordData
} from '../types'
import { calculateBlockDuration } from './blocking'
import { formatDuration, safeExecAsync } from '../utils/helpers'

// ── Internal result type ───────────────────────────────────────────────────────

interface BlockResult {
  unblockTime: number
  blockCount: number
  duration: number
}

// ── Factory ────────────────────────────────────────────────────────────────────

export const createRateLimiter = <
  TReq extends object = Record<string, unknown>
>(
  options: RateLimitOptions<TReq> = {}
): RateLimiterCore<TReq> => {
  const {
    strategy = 'sliding',
    windowMs = 60_000,
    maxRequests = 15,
    initialBlockMs = 20 * 60_000,
    message = {
      success: false,
      message: 'Too many requests. You are temporarily blocked.'
    },
    keyGenerator = () => 'unknown',
    enableLogger = false,
    skip = () => false,
    maxKeys = 10_000,
    whitelist,
    dryRun = false,
    headers = true,
    store: customStore,
    onRecord,
    extractRequestData,
    onBlocked,
    onLimitReached,
    onRequestAllowed
  } = options

  const store = customStore ?? new MemoryStore({ maxKeys })

  // Prevents double-counting when the same instance is mounted at multiple
  // router levels (e.g. app.use('/api', limiter) + router.use(limiter)).
  const checkedRequests = new WeakMap<object, true>()

  let destroyed = false

  const log = (...args: unknown[]): void => {
    if (enableLogger) console.log(`[rate-shield:${strategy}]`, ...args)
  }

  // ── Whitelist ──────────────────────────────────────────────────────────────

  const isWhitelisted = (key: string, req: TReq): boolean => {
    if (!whitelist) return false
    if (Array.isArray(whitelist)) return whitelist.includes(key)
    try {
      return whitelist(key, req)
    } catch {
      return false
    }
  }

  // ── Response helpers ───────────────────────────────────────────────────────

  const buildPayload = (durationMs: number): unknown => {
    if (typeof message === 'string') return message
    return {
      ...message,
      message: `Too many requests. You are blocked for ${formatDuration(durationMs)}.`,
      retryAfter: Math.ceil(durationMs / 1000)
    }
  }

  const writeHeaders = (
    ctx: RateShieldContext<TReq>,
    remaining: number,
    resetEpochSec: number,
    retryAfterSec?: number
  ): void => {
    if (!headers || ctx.isResponseSent()) return
    ctx.setHeader('X-RateLimit-Limit', maxRequests)
    ctx.setHeader('X-RateLimit-Remaining', Math.max(0, remaining))
    ctx.setHeader('X-RateLimit-Reset', resetEpochSec)
    ctx.setHeader('X-RateLimit-Window', Math.ceil(windowMs / 1000))
    ctx.setHeader('X-RateLimit-Strategy', strategy)
    if (dryRun) ctx.setHeader('X-RateLimit-DryRun', 'true')
    if (retryAfterSec !== undefined)
      ctx.setHeader('Retry-After', Math.ceil(retryAfterSec))
  }

  // ── Block enforcement ──────────────────────────────────────────────────────

  const enforceBlock = async (
    ctx: RateShieldContext<TReq>,
    key: string,
    now: number,
    existingBlock: BlockRecord | null
  ): Promise<BlockResult> => {
    const history = [...(existingBlock?.blockHistory ?? [])]
    const duration = calculateBlockDuration(history, initialBlockMs)
    const unblockTime = now + duration
    const blockCount = (existingBlock?.blockCount ?? 0) + 1

    history.push({ duration, timestamp: now })
    // Keep only the last 30 days of violations to bound store growth.
    const trimmedHistory = history.filter(
      b => now - b.timestamp < 30 * 24 * 60 * 60_000
    )

    await store.setBlock(key, {
      unblockTime,
      blockCount,
      lastBlockDuration: duration,
      blockHistory: trimmedHistory
    })
    // Remove tracker so a fresh window begins after unblock.
    await store.deleteEntry(key)

    log(
      `BLOCKED "${key}" for ${formatDuration(duration)} (offense #${trimmedHistory.length})`
    )

    if (onBlocked) {
      await safeExecAsync('onBlocked', () =>
        onBlocked!({
          key,
          req: ctx.req,
          blockDuration: duration,
          blockCount,
          unblockTime
        } as BlockedEvent<TReq>)
      )
    }

    const retryAfterSec = Math.ceil(duration / 1000)
    writeHeaders(ctx, 0, Math.ceil(unblockTime / 1000), retryAfterSec)

    if (dryRun) {
      log(`[DryRun] Block recorded for "${key}" — request allowed through.`)
    } else {
      if (!ctx.isResponseSent()) ctx.block(429, buildPayload(duration))
    }

    return { unblockTime, blockCount, duration }
  }

  // ── onRecord helpers ───────────────────────────────────────────────────────

  const emitRecord = async (data: RecordData<TReq>): Promise<void> => {
    if (!onRecord) return
    await safeExecAsync('onRecord', () => onRecord!(data))
  }

  // With exactOptionalPropertyTypes, we must omit `requestData` entirely rather
  // than set it to `undefined` when extractRequestData is not configured.
  const withRequestData = (
    base: Omit<RecordData<TReq>, 'requestData'>
  ): RecordData<TReq> =>
    extractRequestData
      ? { ...base, requestData: extractRequestData(base.req) }
      : (base as RecordData<TReq>)

  // ── Strategy: fixed window ─────────────────────────────────────────────────

  const handleFixed = async (
    key: string,
    now: number,
    ctx: RateShieldContext<TReq>
  ): Promise<void> => {
    const raw = await store.getEntry(key)
    let entry: FixedWindowEntry

    if (!raw || raw.type !== 'fixed' || raw.resetTime <= now) {
      entry = { type: 'fixed', count: 0, resetTime: now + windowMs }
      log(`New fixed window for "${key}"`)
    } else {
      entry = raw
    }

    entry.count++
    await store.setEntry(key, entry)

    const remaining = maxRequests - entry.count
    log(`[Fixed] "${key}": ${entry.count}/${maxRequests}`)

    writeHeaders(ctx, remaining, Math.ceil(entry.resetTime / 1000))

    if (entry.count > maxRequests) {
      const existingBlock = await store.getBlock(key)
      const result = await enforceBlock(ctx, key, now, existingBlock)

      await emitRecord(
        withRequestData({
          key,
          req: ctx.req,
          strategy,
          count: entry.count,
          remaining: 0,
          resetTime: result.unblockTime,
          isBlocked: true,
          dryRunBlocked: dryRun,
          blockCount: result.blockCount,
          blockExpiresAt: result.unblockTime,
          timestamp: now
        })
      )

      if (!dryRun) return
      return ctx.pass()
    }

    if (onRequestAllowed) {
      await safeExecAsync('onRequestAllowed', () =>
        onRequestAllowed!({
          key,
          req: ctx.req,
          count: entry.count,
          remaining: Math.max(0, remaining)
        } as AllowedEvent<TReq>)
      )
    }

    // remaining === 0 → this was the last slot; the next request will block.
    if (onLimitReached && remaining === 0) {
      await safeExecAsync('onLimitReached', () =>
        onLimitReached!({
          key,
          req: ctx.req,
          count: entry.count,
          limit: maxRequests
        } as LimitReachedEvent<TReq>)
      )
    }

    await emitRecord(
      withRequestData({
        key,
        req: ctx.req,
        strategy,
        count: entry.count,
        remaining: Math.max(0, remaining),
        resetTime: entry.resetTime,
        isBlocked: false,
        dryRunBlocked: false,
        timestamp: now
      })
    )

    ctx.pass()
  }

  // ── Strategy: sliding window ───────────────────────────────────────────────

  const handleSliding = async (
    key: string,
    now: number,
    ctx: RateShieldContext<TReq>
  ): Promise<void> => {
    const raw = await store.getEntry(key)
    let entry: SlidingWindowEntry

    if (!raw || raw.type !== 'sliding') {
      entry = { type: 'sliding', timestamps: [] }
    } else {
      entry = raw
    }

    // Slide: discard timestamps outside the rolling window.
    entry.timestamps = entry.timestamps.filter(ts => ts > now - windowMs)

    const count = entry.timestamps.length
    const remaining = maxRequests - count
    const resetTime =
      count > 0 ? entry.timestamps[0]! + windowMs : now + windowMs

    log(`[Sliding] "${key}": ${count}/${maxRequests} in last ${windowMs}ms`)

    writeHeaders(ctx, remaining, Math.ceil(resetTime / 1000))

    if (count >= maxRequests) {
      const existingBlock = await store.getBlock(key)
      const result = await enforceBlock(ctx, key, now, existingBlock)

      await emitRecord(
        withRequestData({
          key,
          req: ctx.req,
          strategy,
          count,
          remaining: 0,
          resetTime: result.unblockTime,
          isBlocked: true,
          dryRunBlocked: dryRun,
          blockCount: result.blockCount,
          blockExpiresAt: result.unblockTime,
          timestamp: now
        })
      )

      if (!dryRun) return
      return ctx.pass()
    }

    entry.timestamps.push(now)
    await store.setEntry(key, entry)

    if (onRequestAllowed) {
      await safeExecAsync('onRequestAllowed', () =>
        onRequestAllowed!({
          key,
          req: ctx.req,
          count: count + 1,
          remaining: Math.max(0, remaining - 1)
        } as AllowedEvent<TReq>)
      )
    }

    // remaining === 1 before push → 0 after → next request blocks.
    if (onLimitReached && remaining === 1) {
      await safeExecAsync('onLimitReached', () =>
        onLimitReached!({
          key,
          req: ctx.req,
          count: count + 1,
          limit: maxRequests
        } as LimitReachedEvent<TReq>)
      )
    }

    await emitRecord(
      withRequestData({
        key,
        req: ctx.req,
        strategy,
        count: count + 1,
        remaining: Math.max(0, remaining - 1),
        resetTime,
        isBlocked: false,
        dryRunBlocked: false,
        timestamp: now
      })
    )

    ctx.pass()
  }

  // ── Cleanup timer ──────────────────────────────────────────────────────────

  const runCleanup = (): void => {
    if (destroyed) return
    void (async () => {
      try {
        await store.cleanup?.(windowMs)
        log('Cleanup complete.')
      } catch (err) {
        console.error('[rate-shield] Cleanup error:', err)
      }
    })()
  }

  const cleanupInterval = setInterval(runCleanup, 2 * 60_000)
  // .unref() prevents the interval from keeping the Node.js event loop alive,
  // which would cause Jest / Vitest to hang after tests complete.
  if (typeof cleanupInterval.unref === 'function') cleanupInterval.unref()

  const onProcessExit = (): void => clearInterval(cleanupInterval)
  process.once('exit', onProcessExit)

  // ── Core instance ──────────────────────────────────────────────────────────

  return {
    async handle(ctx: RateShieldContext<TReq>): Promise<void> {
      if (destroyed) {
        console.warn(
          '[rate-shield] This instance has been destroyed — call createRateLimiter() to get a new one.'
        )
        return ctx.pass()
      }

      let shouldSkip = false
      try {
        shouldSkip = await skip(ctx.req)
      } catch (err) {
        console.error('[rate-shield] Error in skip():', err)
      }
      if (shouldSkip) return ctx.pass()

      if (checkedRequests.has(ctx.req)) {
        log('Duplicate check on same request — skipping.')
        return ctx.pass()
      }
      checkedRequests.set(ctx.req, true)

      let key = 'unknown'
      try {
        key = keyGenerator(ctx.req)
      } catch (err) {
        console.error('[rate-shield] Error in keyGenerator():', err)
      }

      const now = Date.now()
      log(`Request from "${key}"`)

      if (isWhitelisted(key, ctx.req)) {
        log(`"${key}" is whitelisted — bypassing.`)
        return ctx.pass()
      }

      // ── Active block check ────────────────────────────────────────────────
      const blockRecord = await store.getBlock(key)

      if (blockRecord && blockRecord.unblockTime > now) {
        const remainingMs = blockRecord.unblockTime - now
        log(`"${key}" is blocked — ${formatDuration(remainingMs)} remaining.`)

        await emitRecord(
          withRequestData({
            key,
            req: ctx.req,
            strategy,
            count: maxRequests,
            remaining: 0,
            resetTime: blockRecord.unblockTime,
            isBlocked: true,
            dryRunBlocked: dryRun,
            blockCount: blockRecord.blockCount,
            blockExpiresAt: blockRecord.unblockTime,
            timestamp: now
          })
        )

        if (dryRun) {
          log(`[DryRun] Would reject "${key}" — allowing through.`)
          return ctx.pass()
        }

        writeHeaders(
          ctx,
          0,
          Math.ceil(blockRecord.unblockTime / 1000),
          Math.ceil(remainingMs / 1000)
        )
        if (!ctx.isResponseSent()) ctx.block(429, buildPayload(remainingMs))
        return
      }

      if (blockRecord) {
        await store.deleteBlock(key)
        log(`Block expired — "${key}" is now unblocked.`)
      }

      if (strategy === 'sliding') {
        return handleSliding(key, now, ctx)
      }
      return handleFixed(key, now, ctx)
    },

    async destroy(): Promise<void> {
      if (destroyed) return
      destroyed = true
      clearInterval(cleanupInterval)
      process.removeListener('exit', onProcessExit)
      await store.destroy?.()
      log('Instance destroyed — all state cleared.')
    },

    async reset(key: string): Promise<void> {
      await store.resetKey(key)
      log(`State reset for "${key}".`)
    },

    async resetAll(): Promise<void> {
      await store.resetAll()
      log('All state cleared.')
    },

    async getInfo(key: string): Promise<RateLimitInfo | null> {
      const now = Date.now()
      const block = await store.getBlock(key)

      if (block && block.unblockTime > now) {
        return {
          key,
          strategy,
          count: maxRequests,
          remaining: 0,
          resetTime: block.unblockTime,
          isBlocked: true,
          blockExpiresAt: block.unblockTime,
          blockCount: block.blockCount
        }
      }

      const entry = await store.getEntry(key)
      if (!entry) return null

      if (entry.type === 'fixed') {
        return {
          key,
          strategy,
          count: entry.count,
          remaining: Math.max(0, maxRequests - entry.count),
          resetTime: entry.resetTime,
          isBlocked: false
        }
      }

      const fresh = entry.timestamps.filter(ts => ts > now - windowMs)
      return {
        key,
        strategy,
        count: fresh.length,
        remaining: Math.max(0, maxRequests - fresh.length),
        resetTime: fresh.length > 0 ? fresh[0]! + windowMs : now + windowMs,
        isBlocked: false
      }
    },

    async getStats(): Promise<RateLimitStats> {
      return {
        strategy,
        trackedKeys: await store.entryCount(),
        blockedKeys: await store.blockedCount(),
        windowMs,
        maxRequests
      }
    }
  }
}
